import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { OptionSelect } from "@/components/data/FieldInput";
import { SimpleTable } from "@/components/data/SimpleTable";
import { api, errMsg, fmtMoney, monthStart, today } from "@/lib/api";

const GROUPES = [["categorie", "Par catégorie"], ["mois", "Par période (mois)"], ["utilisateur", "Par utilisateur"], ["statut", "Par statut"]]
  .map(([value, label]) => ({ value, label }));

export function Echeances({ reloadKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/charges/echeances").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [reloadKey]);
  if (!d) return null;
  const rows = d.items.map((i) => ({ ...i, delai: i.jours < 0 ? `En retard de ${-i.jours} j` : i.jours === 0 ? "Aujourd'hui" : `Dans ${i.jours} j` }));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-900" data-testid="echeances-retard">
          <div className="text-[10px] uppercase tracking-[0.18em] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />En retard</div>
          <div className="font-mono text-xl font-bold mt-1">{d.en_retard}</div><div className="text-xs font-mono">{fmtMoney(d.montant_retard)}</div>
        </div>
        <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 text-amber-900" data-testid="echeances-proches">
          <div className="text-[10px] uppercase tracking-[0.18em] flex items-center gap-1"><Clock className="h-3 w-3" />Sous {d.delai} jours</div>
          <div className="font-mono text-xl font-bold mt-1">{d.proches}</div>
        </div>
        <div className="p-4 rounded-lg border bg-card" data-testid="echeances-total">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reste à payer (alertes)</div>
          <div className="font-mono text-xl font-bold mt-1">{fmtMoney(d.montant_total)}</div>
        </div>
      </div>
      <SimpleTable title={`Échéances dépassées ou à moins de ${d.delai} jours`} module="charges" testId="echeances-table" rows={rows} cols={[
        { k: "numero", label: "N°" }, { k: "libelle", label: "Libellé" }, { k: "categorie", label: "Catégorie" }, { k: "fournisseur", label: "Fournisseur" },
        { k: "echeance", label: "Échéance", type: "date" }, { k: "delai", label: "Délai" }, { k: "statut", label: "Statut", type: "badge" },
        { k: "reste", label: "Reste à payer", type: "money" }]} />
      <p className="text-[11px] text-muted-foreground">Délai d'alerte réglable dans Paramètres généraux. Les charges engagées n'affectent pas la trésorerie tant qu'aucun paiement n'est enregistré.</p>
    </div>
  );
}

export function ChargesRapport() {
  const [p, setP] = useState({ group: "categorie", date_from: monthStart(), date_to: today() });
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/charges/rapport", { params: p }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-52"><OptionSelect options={GROUPES} value={p.group} onChange={(v) => v && setP({ ...p, group: v })} testId="charges-rapport-group" /></div>
        <Input type="date" className="h-9 w-[150px]" value={p.date_from} onChange={(e) => setP({ ...p, date_from: e.target.value })} data-testid="charges-rapport-from" />
        <Input type="date" className="h-9 w-[150px]" value={p.date_to} onChange={(e) => setP({ ...p, date_to: e.target.value })} data-testid="charges-rapport-to" />
      </div>
      <SimpleTable title="Rapport des charges" module="charges" testId="charges-rapport-table" rows={rows} cols={[
        { k: "groupe", label: "Groupe" }, { k: "nb", label: "Nb" }, { k: "engage", label: "Engagé", type: "money" },
        { k: "paye", label: "Payé", type: "money" }, { k: "reste", label: "Reste à payer", type: "money" }]} />
      <p className="text-[11px] text-muted-foreground">Charges datées dans la période, hors charges rejetées ou annulées.</p>
    </div>
  );
}
